import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { ArrowLeftIcon } from "./components/icons"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { TimePicker } from "./components/time-picker"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { DetailPageSkeleton } from "./components/skeleton"

type EventType = "workshop" | "showcase" | "graduation" | "field_trip" | "other"
type EventAudience = "participants_guardians" | "participants_only" | "public" | "staff_only"
type EventStatus = "draft" | "published" | "cancelled"
type ParticipantGender = "female" | "male" | "non_binary" | "prefer_not_to_say" | "other"
type AttendanceStatus = "present" | "absent" | "excused"

interface Speaker {
  name: string
  role: string
}

interface Event {
  id: string
  code: string
  programId: string
  programName?: string
  title: string
  type: EventType
  date: string
  startTime: string
  endTime: string | null
  venue: string | null
  audience: EventAudience
  speakers: Speaker[] | null
  bannerUrl: string | null
  description: string | null
  status: EventStatus
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

interface EventParticipant {
  id: string
  eventId: string
  programParticipantId: string | null
  firstName: string
  lastName: string
  age: number | null
  gender: ParticipantGender | null
  email: string | null
  phone: string | null
  relationship: string | null
  attendanceStatus: AttendanceStatus
  notes: string | null
  createdAt: string
  updatedAt: string
}

interface ProgramParticipantOption {
  id: string
  code?: string
  firstName: string
  lastName: string
  email: string | null
  phone?: string | null
}

const EVENT_STATUS_COLORS: Record<EventStatus, { bg: string; text: string }> = {
  draft: { bg: "bg-gold-100", text: "text-gold-700" },
  published: { bg: "bg-green-100", text: "text-green-700" },
  cancelled: { bg: "bg-red-100", text: "text-red-700" },
}

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  workshop: "Workshop",
  showcase: "Showcase",
  graduation: "Graduation",
  field_trip: "Field Trip",
  other: "Other",
}

const AUDIENCE_LABELS: Record<EventAudience, string> = {
  participants_guardians: "Participants + Guardians",
  participants_only: "Participants Only",
  public: "Public",
  staff_only: "Staff Only",
}

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: "workshop", label: "Workshop" },
  { value: "showcase", label: "Showcase" },
  { value: "graduation", label: "Graduation" },
  { value: "field_trip", label: "Field trip" },
  { value: "other", label: "Other" },
]

const AUDIENCE_TYPES: { value: EventAudience; label: string }[] = [
  { value: "participants_guardians", label: "Participants + guardians" },
  { value: "participants_only", label: "Participants only" },
  { value: "public", label: "Public" },
  { value: "staff_only", label: "Staff only" },
]

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  )
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function formatEventDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(":")
  const h = Number.parseInt(hours || "0", 10)
  const period = h >= 12 ? "PM" : "AM"
  const hour12 = h % 12 || 12
  return `${hour12}:${minutes} ${period}`
}

function formatDateForInput(dateString: string): string {
  const date = new Date(dateString)
  return date.toISOString().split("T")[0] || ""
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("events")
  const canMutate = useHasPermission("events_mutate")
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [participants, setParticipants] = useState<EventParticipant[]>([])
  const [participantsLoading, setParticipantsLoading] = useState(true)
  const [showParticipantModal, setShowParticipantModal] = useState(false)
  const [editingParticipant, setEditingParticipant] = useState<EventParticipant | null>(null)
  const [deletingParticipant, setDeletingParticipant] = useState<EventParticipant | null>(null)
  const [removingParticipant, setRemovingParticipant] = useState(false)

  const fetchEvent = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.programs.events[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setEvent(body.data as Event)
      }
    } catch {
      setError("Failed to load event")
    } finally {
      setLoading(false)
    }
  }

  const fetchParticipants = async () => {
    if (!id) return
    setParticipantsLoading(true)
    try {
      const res = await client.api.admin.programs.events[":id"].participants.$get({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) setParticipants(body.data.items as EventParticipant[])
    } catch {
      toast.error("Failed to load event participants")
    } finally {
      setParticipantsLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchEvent()
      void fetchParticipants()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const handleRemoveParticipant = async () => {
    if (!id || !deletingParticipant || !canMutate) return
    setRemovingParticipant(true)
    try {
      const res = await client.api.admin.programs.events[":id"].participants[":participantId"].$delete({
        param: { id, participantId: deletingParticipant.id },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Participant removed from event")
      setDeletingParticipant(null)
      void fetchParticipants()
    } catch {
      toast.error("Failed to remove participant")
    } finally {
      setRemovingParticipant(false)
    }
  }

  const handlePublish = async () => {
    if (!id || !canMutate) return
    setPublishing(true)
    try {
      const res = await client.api.admin.programs.events[":id"].publish.$post({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Event published successfully")
      void fetchEvent()
    } catch {
      toast.error("Failed to publish event")
    } finally {
      setPublishing(false)
    }
  }

  const handleDelete = async () => {
    if (!id || !canMutate) return
    setDeleting(true)
    try {
      const res = await client.api.admin.programs.events[":id"].$delete({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Event deleted successfully")
      navigate("/admin/events")
    } catch {
      toast.error("Failed to delete event")
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Event</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view this event.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !event) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/events")}
          className="inline-flex items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "Event not found"}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/events")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Events
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <p className="text-xs uppercase tracking-wider text-ink/50">{event.code}</p>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                  EVENT_STATUS_COLORS[event.status].bg
                } ${EVENT_STATUS_COLORS[event.status].text}`}
              >
                {event.status}
              </span>
            </div>
            <h1 className="mt-2 font-display text-2xl font-bold text-ink sm:text-3xl">{event.title}</h1>
            {event.programName && (
              <p className="mt-1 text-sm text-ink/60">{event.programName}</p>
            )}
          </div>

          {canMutate && (
            <div className="flex flex-wrap gap-2">
              {event.status === "draft" && (
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="inline-flex items-center gap-2 rounded-full border border-green-600 px-4 py-2 text-sm font-medium text-green-600 transition-colors hover:bg-green-50 disabled:opacity-50"
                >
                  <SendIcon className="h-4 w-4" />
                  {publishing ? "Publishing..." : "Publish"}
                </button>
              )}
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
              >
                <EditIcon className="h-4 w-4" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center gap-2 rounded-full border border-red-300 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Event Details */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Date & Time Card */}
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">When</h2>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3 text-ink">
                <CalendarIcon className="h-5 w-5 text-ink/50" />
                <span>{formatEventDate(event.date)}</span>
              </div>
              <div className="flex items-center gap-3 text-ink">
                <ClockIcon className="h-5 w-5 text-ink/50" />
                <span>
                  {formatTime(event.startTime)}
                  {event.endTime && ` - ${formatTime(event.endTime)}`}
                </span>
              </div>
            </div>
          </div>

          {/* Location Card */}
          {event.venue && (
            <div className="rounded-[10px] border border-gold-500 bg-white p-5">
              <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Where</h2>
              <div className="mt-4 flex items-center gap-3 text-ink">
                <MapPinIcon className="h-5 w-5 text-ink/50" />
                <span>{event.venue}</span>
              </div>
            </div>
          )}

          {/* Description Card */}
          {event.description && (
            <div className="rounded-[10px] border border-gold-500 bg-white p-5">
              <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">About</h2>
              <p className="mt-4 text-ink/80 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Speakers Card */}
          {event.speakers && event.speakers.length > 0 && (
            <div className="rounded-[10px] border border-gold-500 bg-white p-5">
              <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Speakers</h2>
              <div className="mt-4 space-y-3">
                {event.speakers.map((speaker, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-100 text-forest-700">
                      <UsersIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-ink">{speaker.name}</p>
                      <p className="text-sm text-ink/60">{speaker.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Details</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/60">Type</dt>
                <dd className="font-medium text-ink">{EVENT_TYPE_LABELS[event.type]}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/60">Audience</dt>
                <dd className="font-medium text-ink">{AUDIENCE_LABELS[event.audience]}</dd>
              </div>
              {event.publishedAt && (
                <div className="flex justify-between">
                  <dt className="text-ink/60">Published</dt>
                  <dd className="font-medium text-ink">
                    {new Date(event.publishedAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Participants */}
      <section className="rounded-[10px] border border-gold-500 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Participants</h2>
            <p className="mt-1 text-sm text-ink/60">{participants.length} people logged for this event</p>
          </div>
          {canMutate && (
            <button
              onClick={() => {
                setEditingParticipant(null)
                setShowParticipantModal(true)
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900"
            >
              <PlusIcon className="h-4 w-4" />
              Log participant
            </button>
          )}
        </div>

        {participantsLoading ? (
          <p className="mt-6 text-sm text-ink/50">Loading participants...</p>
        ) : participants.length === 0 ? (
          <div className="mt-6 rounded-lg bg-cream-50 p-6 text-center text-sm text-ink/50">No participants logged yet.</div>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-ink/10 text-xs uppercase tracking-wider text-ink/50">
                <tr>
                  <th className="px-3 py-3 font-medium">Name</th>
                  <th className="px-3 py-3 font-medium">Age / gender</th>
                  <th className="px-3 py-3 font-medium">Contact</th>
                  <th className="px-3 py-3 font-medium">Relationship</th>
                  <th className="px-3 py-3 font-medium">Attendance</th>
                  {canMutate && <th className="px-3 py-3 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {participants.map((participant) => (
                  <tr key={participant.id}>
                    <td className="px-3 py-3 align-top">
                      <p className="font-medium text-ink">{participant.firstName} {participant.lastName}</p>
                      {participant.notes && <p className="mt-1 max-w-[220px] text-xs text-ink/50">{participant.notes}</p>}
                    </td>
                    <td className="px-3 py-3 align-top text-ink/70">{participant.age ?? "-"} / {participant.gender?.replaceAll("_", " ") ?? "-"}</td>
                    <td className="px-3 py-3 align-top text-ink/70">
                      <div>{participant.phone || "-"}</div>
                      <div className="text-xs text-ink/50">{participant.email || ""}</div>
                    </td>
                    <td className="px-3 py-3 align-top text-ink/70">{participant.relationship || "-"}</td>
                    <td className="px-3 py-3 align-top">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
                        participant.attendanceStatus === "present" ? "bg-green-100 text-green-700" : participant.attendanceStatus === "absent" ? "bg-red-100 text-red-700" : "bg-gold-100 text-gold-700"
                      }`}>{participant.attendanceStatus}</span>
                    </td>
                    {canMutate && (
                      <td className="px-3 py-3 align-top">
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingParticipant(participant); setShowParticipantModal(true) }} className="text-xs font-medium text-forest-700 hover:text-forest-950">Edit</button>
                          <button onClick={() => setDeletingParticipant(participant)} className="text-xs font-medium text-red-600 hover:text-red-800">Remove</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Edit Modal */}
      <EditEventModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        event={event}
        onSuccess={() => {
          setShowEditModal(false)
          void fetchEvent()
        }}
      />

      <EventParticipantModal
        isOpen={showParticipantModal}
        onClose={() => setShowParticipantModal(false)}
        eventId={event.id}
        programId={event.programId}
        participant={editingParticipant}
        loggedProgramParticipantIds={participants.flatMap((item) => item.programParticipantId ? [item.programParticipantId] : [])}
        onSuccess={() => {
          setShowParticipantModal(false)
          void fetchParticipants()
        }}
      />

      <Modal
        isOpen={Boolean(deletingParticipant)}
        onClose={() => setDeletingParticipant(null)}
        title="Remove participant"
      >
        <p className="text-ink/70">
          Remove {deletingParticipant?.firstName} {deletingParticipant?.lastName} from this event?
        </p>
        <ModalActions>
          <ModalButton onClick={() => setDeletingParticipant(null)}>Cancel</ModalButton>
          <ModalButton onClick={handleRemoveParticipant} variant="danger" disabled={removingParticipant}>
            {removingParticipant ? "Removing..." : "Remove"}
          </ModalButton>
        </ModalActions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Event"
      >
        <p className="text-ink/70">
          Are you sure you want to delete "{event.title}"? This action cannot be undone.
        </p>
        <ModalActions>
          <ModalButton onClick={() => setShowDeleteModal(false)}>Cancel</ModalButton>
          <ModalButton
            onClick={handleDelete}
            variant="danger"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </ModalButton>
        </ModalActions>
      </Modal>
    </div>
  )
}

function EventParticipantModal({
  isOpen,
  onClose,
  eventId,
  programId,
  participant,
  loggedProgramParticipantIds,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  eventId: string
  programId: string
  participant: EventParticipant | null
  loggedProgramParticipantIds: string[]
  onSuccess: () => void
}) {
  const [programParticipants, setProgramParticipants] = useState<ProgramParticipantOption[]>([])
  const [form, setForm] = useState({
    programParticipantId: "",
    firstName: "",
    lastName: "",
    age: "",
    gender: "" as ParticipantGender | "",
    email: "",
    phone: "",
    relationship: "",
    attendanceStatus: "present" as AttendanceStatus,
    notes: "",
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setForm({
      programParticipantId: participant?.programParticipantId || "",
      firstName: participant?.firstName || "",
      lastName: participant?.lastName || "",
      age: participant?.age?.toString() || "",
      gender: participant?.gender || "",
      email: participant?.email || "",
      phone: participant?.phone || "",
      relationship: participant?.relationship || "",
      attendanceStatus: participant?.attendanceStatus || "present",
      notes: participant?.notes || "",
    })
    setError(null)
    void (async () => {
      try {
        const res = await client.api.admin.programs[":id"].participants.$get({
          param: { id: programId },
          query: { limit: "100", offset: "0" },
        })
        const body = await res.json()
        if (res.ok && "data" in body) setProgramParticipants(body.data.items as ProgramParticipantOption[])
      } catch {
        setProgramParticipants([])
      }
    })()
  }, [isOpen, participant, programId])

  const handleProgramParticipantChange = (value: string) => {
    const selected = programParticipants.find((item) => {
      const label = `${item.firstName} ${item.lastName}${item.code ? ` · ${item.code}` : ""}`
      return !loggedProgramParticipantIds.includes(item.id) && label === value
    })
    if (!selected) {
      setForm((current) => ({ ...current, programParticipantId: "" }))
      return
    }
    setForm((current) => ({
      ...current,
      programParticipantId: selected.id,
      firstName: selected.firstName,
      lastName: selected.lastName,
      email: selected.email || "",
      phone: selected.phone || "",
    }))
  }

  const availableProgramParticipants = programParticipants
    .filter((item) => !loggedProgramParticipantIds.includes(item.id))
    .map((item) => ({
      item,
      label: `${item.firstName} ${item.lastName}${item.code ? ` · ${item.code}` : ""}`,
    }))
  const selectedProgramParticipant = availableProgramParticipants.find(({ item }) => item.id === form.programParticipantId)
  const genderOptions = [
    { value: "female" as const, label: "Female" },
    { value: "male" as const, label: "Male" },
    { value: "non_binary" as const, label: "Non-binary" },
    { value: "prefer_not_to_say" as const, label: "Prefer not to say" },
    { value: "other" as const, label: "Other" },
  ]
  const attendanceOptions = [
    { value: "present" as const, label: "Present" },
    { value: "absent" as const, label: "Absent" },
    { value: "excused" as const, label: "Excused" },
  ]

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    const json = {
      programParticipantId: form.programParticipantId || undefined,
      firstName: form.firstName,
      lastName: form.lastName,
      age: form.age ? Number(form.age) : null,
      gender: form.gender || null,
      email: form.email || null,
      phone: form.phone || null,
      relationship: form.relationship || null,
      attendanceStatus: form.attendanceStatus,
      notes: form.notes || null,
    }

    try {
      const res = participant
        ? await client.api.admin.programs.events[":id"].participants[":participantId"].$patch({
            param: { id: eventId, participantId: participant.id },
            json,
          })
        : await client.api.admin.programs.events[":id"].participants.$post({ param: { id: eventId }, json })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      toast.success(participant ? "Participant updated" : "Participant logged")
      onSuccess()
    } catch {
      setError("Failed to save participant")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={participant ? "Edit participant" : "Log participant"} subtitle="One participant can only be logged once per event" maxWidth="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {!participant && (
          <Select
            label="Existing program participant"
            value={selectedProgramParticipant?.label || "Manual entry / guest"}
            onChange={(value) => handleProgramParticipantChange(value === "Manual entry / guest" ? "" : value)}
            options={availableProgramParticipants.map(({ label }) => label)}
            searchable
            searchPlaceholder="Search participants..."
            emptyMessage="No available participants."
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-medium text-ink">First name<input required value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
          <label className="text-sm font-medium text-ink">Last name<input required value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
          <label className="text-sm font-medium text-ink">Age<input type="number" min="0" max="150" value={form.age} onChange={(event) => setForm({ ...form, age: event.target.value })} className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
          <Select label="Gender" value={genderOptions.find((option) => option.value === form.gender)?.label || "Not specified"} onChange={(value) => setForm({ ...form, gender: genderOptions.find((option) => option.label === value)?.value || "" })} options={genderOptions.map((option) => option.label)} clearOption="Not specified" />
          <label className="text-sm font-medium text-ink">Phone<input type="tel" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
          <label className="text-sm font-medium text-ink">Email<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
          <label className="text-sm font-medium text-ink">Relationship<input value={form.relationship} onChange={(event) => setForm({ ...form, relationship: event.target.value })} placeholder="e.g. Guardian" className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
          <Select label="Attendance" value={attendanceOptions.find((option) => option.value === form.attendanceStatus)?.label || "Present"} onChange={(value) => setForm({ ...form, attendanceStatus: attendanceOptions.find((option) => option.label === value)?.value || "present" })} options={attendanceOptions.map((option) => option.label)} />
        </div>
        <label className="block text-sm font-medium text-ink">Notes<textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={3} className="mt-1 w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 font-normal focus:border-forest-700 focus:outline-none" /></label>
        {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}
        <ModalActions><ModalButton onClick={onClose}>Cancel</ModalButton><ModalButton type="submit" variant="primary" disabled={saving || !form.firstName.trim() || !form.lastName.trim()}>{saving ? "Saving..." : participant ? "Save changes" : "Log participant"}</ModalButton></ModalActions>
      </form>
    </Modal>
  )
}

function EditEventModal({
  isOpen,
  onClose,
  event,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  event: Event
  onSuccess: () => void
}) {
  const [title, setTitle] = useState(event.title)
  const [eventType, setEventType] = useState<EventType>(event.type)
  const [date, setDate] = useState(formatDateForInput(event.date))
  const [startTime, setStartTime] = useState(event.startTime)
  const [endTime, setEndTime] = useState(event.endTime || "")
  const [venue, setVenue] = useState(event.venue || "")
  const [audience, setAudience] = useState<EventAudience>(event.audience)
  const [description, setDescription] = useState(event.description || "")
  const [speakers, setSpeakers] = useState<Speaker[]>(event.speakers || [{ name: "", role: "" }])
  const [status, setStatus] = useState<EventStatus>(event.status)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setTitle(event.title)
    setEventType(event.type)
    setDate(formatDateForInput(event.date))
    setStartTime(event.startTime)
    setEndTime(event.endTime || "")
    setVenue(event.venue || "")
    setAudience(event.audience)
    setDescription(event.description || "")
    setSpeakers(event.speakers || [{ name: "", role: "" }])
    setStatus(event.status)
  }, [event])

  const handleAddSpeaker = () => {
    setSpeakers([...speakers, { name: "", role: "" }])
  }

  const handleRemoveSpeaker = (index: number) => {
    setSpeakers(speakers.filter((_, i) => i !== index))
  }

  const handleSpeakerChange = (index: number, field: "name" | "role", value: string) => {
    const updated = [...speakers]
    const speaker = updated[index]
    if (speaker) {
      speaker[field] = value
      setSpeakers(updated)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const validSpeakers = speakers.filter(s => s.name.trim() && s.role.trim())

      const res = await client.api.admin.programs.events[":id"].$patch({
        param: { id: event.id },
        json: {
          title,
          type: eventType,
          date,
          startTime,
          endTime: endTime || null,
          venue: venue || null,
          audience,
          description: description || null,
          speakers: validSpeakers.length > 0 ? validSpeakers : null,
          status,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }

      toast.success("Event updated successfully")
      onSuccess()
    } catch {
      setError("Failed to update event")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Event"
      subtitle={event.code}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Event Title & Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink">Event title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <Select
            label="Event type"
            value={EVENT_TYPES.find(t => t.value === eventType)?.label || "Workshop"}
            onChange={(label) => {
              const type = EVENT_TYPES.find(t => t.label === label)
              if (type) setEventType(type.value)
            }}
            options={EVENT_TYPES.map(t => t.label)}
          />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-3 gap-4">
          <DatePicker
            label="Date"
            value={date}
            onChange={setDate}
          />
          <TimePicker
            label="Starts"
            value={startTime}
            onChange={setStartTime}
          />
          <TimePicker
            label="Ends"
            value={endTime}
            onChange={setEndTime}
          />
        </div>

        {/* Venue */}
        <div>
          <label className="block text-sm font-medium text-ink">Venue</label>
          <input
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-ink">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        {/* Audience */}
        <Select
          label="Audience"
          value={AUDIENCE_TYPES.find(t => t.value === audience)?.label || "Participants + guardians"}
          onChange={(label) => {
            const type = AUDIENCE_TYPES.find(t => t.label === label)
            if (type) setAudience(type.value)
          }}
          options={AUDIENCE_TYPES.map(t => t.label)}
        />

        {/* Speakers */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-ink">Speakers / facilitators</label>
            <button
              type="button"
              onClick={handleAddSpeaker}
              className="inline-flex items-center gap-1 text-sm text-forest-700 hover:text-forest-900"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {speakers.map((speaker, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={speaker.name}
                  onChange={(e) => handleSpeakerChange(index, "name", e.target.value)}
                  placeholder="Speaker name"
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
                <input
                  type="text"
                  value={speaker.role}
                  onChange={(e) => handleSpeakerChange(index, "role", e.target.value)}
                  placeholder="Role / topic"
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
                {speakers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSpeaker(index)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-ink/50 hover:bg-cream-100 hover:text-ink"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Status */}
        <Select
          label="Status"
          value={status.charAt(0).toUpperCase() + status.slice(1)}
          onChange={(value) => setStatus(value.toLowerCase() as EventStatus)}
          options={["Draft", "Published", "Cancelled"]}
        />

        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton type="submit" variant="primary" disabled={saving || !title.trim() || !date}>
            {saving ? "Saving..." : "Save Changes"}
          </ModalButton>
        </ModalActions>
      </form>
    </Modal>
  )
}
